import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@app/classes';
import { AnalyticEvents } from '@app/enums';
import { JobPostingTable } from '@app/modules/applicant-tracker/services/job-posting-table/job-posting.table';
import { RecruitingModuleMenuService } from '@app/modules/applicant-tracker/services/menus/recruiting-module-menu.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { IndexView } from '@common/views';
import { JobBoard } from '@models/recruiting/job-board.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';

@Component({
    templateUrl: './job-posting-index.template.html',
    styleUrls: ['./job-posting-index.style.scss'],
})
export class JobPostingIndexView extends IndexView implements OnInit {
    jobBoard: JobBoard;

    isLoading = false;
    canCreateJobPosting = false;
    jobBoardNotConfigured = false;
    isDeleteJobPostingDialogOpen = false;
    table: JobPostingTable;
    jobPostingSelected: JobPosting | null = null;

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private router: Router,
        public recruitingModuleMenuService: RecruitingModuleMenuService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.canCreateJobPosting = this.auth.can(Platform.recruiting.permission.recruiter);

        try {
            this.table = new JobPostingTable(this.auth);

            await this.getJobBoard();

            this.jobBoardNotConfigured = this.isJobBoardNotConfigured();
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onDelete(): Promise<void> {
        this.closeDeleteJobPostingDialog();

        this.isLoading = true;

        try {
            await this.jobPostingSelected.delete();

            if (this.jobPostingSelected.draftAt) {
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingMeatballDeleteJobPostingDraftDelete);
            }

            await this.table.reload();
        } catch (err) {
            this.notify.error(err.message || 'Could not delete job posting');
        }

        this.isLoading = false;
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const jobPosting = data as JobPosting;

        switch (menuItem.event) {
            case JobPostingTable.deleteOption.event:
                this.jobPostingSelected = jobPosting;
                this.isDeleteJobPostingDialogOpen = true;
                break;
            case JobPostingTable.editOption.event:
                if (jobPosting.draftAt) {
                    this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingEditDraft);
                }
                this.router.navigate(['/', 'recruiting', 'jobPostings', 'create'], {
                    queryParams: {
                        jobPostingId: jobPosting.id,
                    },
                });
        }
    }

    closeDeleteJobPostingDialog(): void {
        this.isDeleteJobPostingDialogOpen = false;
    }

    trackJobPostingCreation(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingCreateButton);
    }

    private async getJobBoard(): Promise<void> {
        const [[jobBoard]] = await JobBoard.get();
        this.jobBoard = jobBoard;
    }

    private isJobBoardNotConfigured(): boolean {
        if (!this.jobBoard || !this.jobBoard.companySlug) {
            return true;
        }

        return false;
    }
}
